import React from 'react';
import { GraduationCap, Award, BadgeCheck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const DoctorsSection = () => {
  const { language, t } = useLanguage();

  const doctors = [
    {
      id: 1,
      name: 'Dra. Abril Elizabeth Cañas Sapien',
      title: language === 'en' ? 'Surgeon' : 'Médico Cirujano',
      image: 'https://i0.wp.com/bianhi.com/wp-content/uploads/Dra-Abril-Elizabeth-Canas-Sapien-equipo.jpg?fit=450%2C617&ssl=1',
      cedula: '7768276',
      education: 'Universidad Xochicalco',
      specialization: language === 'en' ? 'Hair Restoration Specialist' : 'Especialista en Restauración Capilar',
      bio: language === 'en'
        ? 'Originally from Guadalajara, she completed her studies at Universidad Xochicalco with undergraduate rotations at Hospital Issstecali Mirador. With extensive experience in hospital administration and patient care, she has been part of Dr. Miguel Angel Parra\'s medical team for 7 years, performing pre and post-operative care.'
        : 'Originaria de Guadalajara, realizó sus estudios en la Universidad Xochicalco con rotación de pregrado en Hospital Issstecali Mirador. Con amplia experiencia en administración hospitalaria y atención al paciente, es parte del equipo médico del Dr. Miguel Ángel Parra desde hace 7 años, desempeñando labores en atención pre y postoperatoria.',
      experience: language === 'en' ? '7+ years' : '7+ años'
    },
    {
      id: 2,
      name: 'Dra. Kenia Sarahi Cañas Sapien',
      title: language === 'en' ? 'Surgeon' : 'Médico Cirujano',
      image: 'https://i0.wp.com/bianhi.com/wp-content/uploads/Dra-Kenia-Sarahi-Canas-Sapien-equipo.jpg?fit=450%2C617&ssl=1',
      cedula: '10045294',
      education: 'UABC',
      specialization: language === 'en' ? 'Hair Restoration Specialist' : 'Especialista en Restauración Capilar',
      bio: language === 'en'
        ? 'Originally from Tijuana, she completed her General Medicine studies at UABC. She has received extensive training in Hair Grafting in Mexico City and Madrid, Spain. Known for achieving results with high naturalness, density, and donor area conservation, she has performed thousands of procedures in her 7 years of experience.'
        : 'Originaria de Tijuana, realizó sus estudios de Medicina General en la UABC. Ha recibido extensa capacitación en Injerto Capilar en Ciudad de México y Madrid, España. Reconocida por lograr resultados con alta naturalidad, densidad y conservación de zona donadora, ha realizado miles de procedimientos en sus 7 años de experiencia.',
      experience: language === 'en' ? '7+ years' : '7+ años'
    }
  ];

  return (
    <section className="py-20 md:py-28 bg-[#F0FDFA]" data-testid="doctors-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-sm font-semibold tracking-wider uppercase text-[#009d9f] mb-4">
            {t('doctors.tagline')}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('doctors.title')}{' '}
            <span className="text-[#009d9f]">{t('doctors.titleHighlight')}</span>
          </h2>
          <p className="text-lg text-slate-600">{t('doctors.description')}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12" data-testid="doctors-grid">
          {doctors.map((doctor, index) => (
            <div
              key={doctor.id}
              className="bg-white rounded-3xl overflow-hidden shadow-lg border border-[#CCFBF1] hover:shadow-2xl transition-all duration-300"
              data-testid={`doctor-card-${index}`}
            >
              <div className="md:flex">
                <div className="md:w-2/5 relative">
                  <img src={doctor.image} alt={doctor.name} className="w-full h-72 md:h-full object-cover object-top" loading="lazy" />
                  <div className="absolute bottom-4 left-4 right-4 md:hidden">
                    <div className="bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-lg">
                      <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{doctor.name}</h3>
                      <p className="text-[#009d9f] font-medium text-sm">{doctor.title}</p>
                    </div>
                  </div>
                </div>

                <div className="md:w-3/5 p-6 md:p-8">
                  <div className="hidden md:block mb-4">
                    <h3 className="text-xl lg:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{doctor.name}</h3>
                    <p className="text-[#009d9f] font-semibold">{doctor.title}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-[#CCFBF1] flex items-center justify-center">
                        <GraduationCap className="w-4 h-4 text-[#009d9f]" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">{t('doctors.education')}</p>
                        <p className="text-xs font-medium text-slate-700 line-clamp-1">{doctor.education}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-[#CCFBF1] flex items-center justify-center">
                        <BadgeCheck className="w-4 h-4 text-[#009d9f]" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">{t('doctors.license')}</p>
                        <p className="text-xs font-medium text-slate-700">{doctor.cedula}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-[#CCFBF1] flex items-center justify-center">
                        <Award className="w-4 h-4 text-[#009d9f]" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">{t('doctors.experience')}</p>
                        <p className="text-xs font-medium text-slate-700">{doctor.experience}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <span className="inline-block text-xs font-semibold text-[#009d9f] bg-[#CCFBF1] px-3 py-1.5 rounded-full">
                      {doctor.specialization}
                    </span>
                  </div>

                  <p className="text-slate-600 text-sm leading-relaxed">{doctor.bio}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12 max-w-2xl mx-auto">
          <p className="text-slate-600">
            {t('doctors.teamNote')}{' '}
            <span className="font-semibold text-slate-800">Dr. Miguel Angel Parra Esquivel</span>,{' '}
            {t('doctors.teamNoteEnd')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default DoctorsSection;
